// src/pages/Inventory/InventoryPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import T from '../../components/common/T';
import InventoryListView from "./InventoryListView";
import StockUsageView from "../Material/InternalPicking/StockUsageView";
import { useInventory } from "../../context/InventoryContext";
import { useNavigate } from "react-router-dom";
import BulkImportControl from '../../components/common/BulkImportControl';
import { useAuth } from "../../context/AuthContext";
import RoleGate from "../../components/RoleGate";
import { API_BASE_URL } from '../../api/axiosConfig';
import useModuleFilters from "../../hooks/useModuleFilters";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel } from "../../components/common/PageLayout";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import {
  ArchiveBoxIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { 
  X, Edit, Layers 
} from 'lucide-react';

const InventoryPage = () => {
  const navigate = useNavigate();
  const { inventories, fetchInventory } = useInventory();
  const { checkAccess } = useAuth();

  useEffect(() => {
    if (!checkAccess('inventory.view')) {
      navigate('/admin/dashboard');
      return;
    }
    if (fetchInventory) fetchInventory();
  }, [checkAccess, navigate, fetchInventory]);

  const [activeTab, setActiveTab] = useState("stock");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ category: "", status: "" });
  const [selectedItem, setSelectedItem] = useState(null); // 🟢 For Split Pane

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
    clearFilters,
    clearingFilters,
  } = useModuleFilters("inventory", {
    categories: [],
    statuses: [],
    locations: [],
  });

  const handleClearAll = () => {
    setFilters({ category: "", status: "" });
    setSearchTerm("");
    if (fetchInventory) fetchInventory();
  };

  const filteredInventory = useMemo(() => {
    return (inventories || []).filter((item) => {
      const matchesSearch = !searchTerm ||
        item.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.sku?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filters.category ? item.category === filters.category : true;
      const matchesStatus = filters.status ? item.status === filters.status : true;
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [inventories, searchTerm, filters]);

  // 🟢 DYNAMIC INVENTORY KPIs
  const statsCounts = useMemo(() => {
    const list = inventories || [];
    return {
      totalItems: list.length,
      inStock: list.filter(i => Number(i.totalQuantity) > (i.reorder_level || 5)).length,
      lowStock: list.filter(i => Number(i.totalQuantity) > 0 && Number(i.totalQuantity) <= (i.reorder_level || 5)).length,
      outOfStock: list.filter(i => Number(i.totalQuantity) <= 0).length,
    };
  }, [inventories]);

  const stats = useMemo(() => [
    {
      label: "Total SKU",
      value: statsCounts.totalItems,
      icon: ArchiveBoxIcon,
      variant: "default",
      isActive: !filters.status,
      onClick: () => setFilters(prev => ({ ...prev, status: "" })),
    },
    {
      label: "Healthy Stock",
      value: statsCounts.inStock,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: filters.status === "In Stock",
      onClick: () => setFilters(prev => ({ ...prev, status: prev.status === "In Stock" ? "" : "In Stock" })),
    },
    {
      label: "Low Stock",
      value: statsCounts.lowStock,
      icon: ExclamationTriangleIcon,
      variant: "orange",
      isActive: filters.status === "Low Stock",
      onClick: () => setFilters(prev => ({ ...prev, status: prev.status === "Low Stock" ? "" : "Low Stock" })),
    },
    {
      label: "Out of Stock",
      value: statsCounts.outOfStock,
      icon: XCircleIcon,
      variant: "red",
      isActive: filters.status === "Out of Stock",
      onClick: () => setFilters(prev => ({ ...prev, status: prev.status === "Out of Stock" ? "" : "Out of Stock" })),
    },
  ], [statsCounts, filters.status]);

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const handleRefresh = () => window.location.reload();

  if (!checkAccess('inventory.view')) return null;

  return (
    <PageLayout>
      {/* 1. HEADER */}
      <PageHeader
        icon={ArchiveBoxIcon}
        iconColor="text-[#E8612C]"
        title="Inventory Management"
        subtitle="Track stock levels, valuation, and warehouse movements"
        onRefresh={handleRefresh}
      >
        <RoleGate permission="inventory.manage">
          <BulkImportControl 
            tableName="Inventory"
            columns={[
              { header: 'Item Name', sample: 'Portland Cement' },
              { header: 'SKU', sample: 'CEM-001' },
              { header: 'Category', sample: 'Cement' },
              { header: 'Unit', sample: 'Bag' },
              { header: 'Unit Price', sample: '350' },
              { header: 'Opening Stock', sample: '100' },
              { header: 'Reorder Level', sample: '20' }
            ]}
            apiEndpoint={`${API_BASE_URL}/api/inventory/bulk-import`}
            onRefresh={handleRefresh} 
          />
        </RoleGate>
        <RoleGate permission="inventory.manage">
          <TopPrimaryButton
            onClick={() => navigate('/admin/inventory/add')}
            label="Add Item"
          />
        </RoleGate>
      </PageHeader>

      {/* 2. KPI DASHBOARD */}
      <StatsGrid stats={stats} cols={4} />

      {/* 3. TOOLBAR */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search SKU or Item Name..."
        tabs={['All', 'In Stock', 'Low Stock', 'Out of Stock']}
        activeTab={filters.status || 'All'}
        onTabChange={(st) => handleFilterChange('status', st === 'All' ? '' : st)}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      >
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#21262d] p-0.5 rounded-lg border border-gray-200 dark:border-[#30363d]">
          <button
            type="button"
            onClick={() => setActiveTab("stock")}
            className={`h-7 px-2.5 text-[11px] font-bold uppercase rounded-md transition-all cursor-pointer ${
              activeTab === 'stock'
                ? 'bg-white dark:bg-[#161b22] text-[#E8612C] shadow-xs border border-gray-200 dark:border-[#30363d]'
                : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Stock
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("usage")}
            className={`h-7 px-2.5 text-[11px] font-bold uppercase rounded-md transition-all cursor-pointer ${
              activeTab === 'usage'
                ? 'bg-white dark:bg-[#161b22] text-[#E8612C] shadow-xs border border-gray-200 dark:border-[#30363d]'
                : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Movements & Usage
          </button>
        </div>
      </Toolbar>

      {/* FILTER PANEL */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        isClearing={clearingFilters}
        onClear={handleClearAll}
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Category</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange("category", e.target.value)}
            disabled={loadingFilters}
            className="w-full h-8 px-2.5 text-xs bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="">All Categories</option>
            {(filterOptions.categories?.length > 0 ? filterOptions.categories : ["Cement", "Steel", "Aggregates", "Electronics"]).map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </FilterPanel>

      {/* 4. CONTENT */}
      {activeTab === "stock" ? (
        <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0 overflow-hidden">
          
          {/* Left: List */}
          <div className={`flex-1 bg-white dark:bg-[#161b22] rounded-xl shadow-xs border border-gray-200 dark:border-[#30363d] overflow-hidden flex flex-col min-h-0 transition-all duration-300 ${selectedItem ? 'hidden lg:flex' : 'flex'}`}>
            <InventoryListView inventories={filteredInventory} onSelect={setSelectedItem} />
          </div>

          {/* Right: Slide-in Detail Panel */}
          {selectedItem && (
            <div className="w-full lg:w-[400px] xl:w-[450px] bg-white dark:bg-[#161b22] rounded-xl shadow-lg border border-gray-200 dark:border-[#30363d] flex flex-col overflow-hidden animate-slide-in-right shrink-0 z-10 h-[calc(100vh-300px)]">
              <div className="bg-gray-50 dark:bg-[#21262d] px-4 py-3 border-b border-gray-200 dark:border-[#30363d] flex justify-between items-center shrink-0">
                <h3 className="font-bold text-gray-800 dark:text-white text-sm"><T>Item Details</T></h3>
                <button onClick={() => setSelectedItem(null)} className="text-gray-400 hover:text-gray-800 dark:hover:text-white transition cursor-pointer"><X size={16} /></button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="border-b border-gray-100 dark:border-[#30363d] pb-3">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{selectedItem.itemName}</h2>
                  <p className="text-xs text-gray-500 dark:text-slate-400 font-mono mt-1">SKU: {selectedItem.sku || '-'}</p>
                  <div className="mt-2">
                    <span className={`px-2 py-0.5 rounded-[4px] text-[10px] font-bold uppercase ${Number(selectedItem.totalQuantity) > (selectedItem.reorder_level || 5) ? 'bg-green-100 dark:bg-emerald-950/40 text-green-700 dark:text-emerald-400' : Number(selectedItem.totalQuantity) > 0 ? 'bg-orange-100 dark:bg-amber-950/40 text-orange-700 dark:text-amber-400' : 'bg-red-100 dark:bg-rose-950/40 text-red-700 dark:text-rose-400'}`}>
                      {Number(selectedItem.totalQuantity) > (selectedItem.reorder_level || 5) ? 'In Stock' : Number(selectedItem.totalQuantity) > 0 ? 'Low Stock' : 'Out of Stock'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-orange-50 dark:bg-[#21262d] border border-orange-100 dark:border-[#30363d] p-3 rounded-lg">
                    <p className="text-[9px] text-[#E8612C] font-bold uppercase mb-0.5"><T>Total Stock</T></p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{selectedItem.totalQuantity} <span className="text-xs font-medium text-gray-500 dark:text-slate-400">{selectedItem.unit}</span></p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#21262d] border border-gray-200 dark:border-[#30363d] p-3 rounded-lg">
                    <p className="text-[9px] text-gray-500 dark:text-slate-400 font-bold uppercase mb-0.5"><T>Unit Price</T></p>
                    <p className="text-xl font-bold text-gray-800 dark:text-white">₹{selectedItem.unit_price || '0.00'}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-[10px] font-bold uppercase text-gray-400 tracking-wider mb-1"><T>Warehouse Locations</T></h4>
                  {selectedItem.locations && selectedItem.locations.length > 0 ? (
                    selectedItem.locations.map((loc, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-white dark:bg-[#21262d] p-2 border border-gray-200 dark:border-[#30363d] rounded text-xs">
                        <div className="flex items-center gap-2">
                          <Layers size={14} className="text-gray-400" />
                          <span className="font-bold text-gray-700 dark:text-slate-200">{loc.warehouseName}</span>
                          <span className="text-gray-400">({loc.bin})</span>
                        </div>
                        <span className="font-bold text-gray-900 dark:text-white">{loc.qty} {selectedItem.unit}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400 italic">No stock locations recorded.</p>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-[#21262d] px-4 py-3 border-t border-gray-200 dark:border-[#30363d] flex justify-end gap-2 shrink-0">
                <button onClick={() => setSelectedItem(null)} className="px-4 py-1.5 bg-white dark:bg-[#161b22] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-200 rounded text-xs font-bold hover:bg-gray-100 dark:hover:bg-slate-800 transition cursor-pointer">Close</button>
                <button onClick={() => navigate(`/admin/inventory/edit/${selectedItem.id}`)} className="px-4 py-1.5 bg-[#E8612C] text-white rounded text-xs font-bold hover:bg-[#cf4f1d] transition flex items-center gap-1 shadow-xs cursor-pointer"><Edit size={12}/> Edit Item</button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-[#161b22] p-4 rounded-xl shadow-xs border border-gray-200 dark:border-[#30363d]">
          <StockUsageView />
        </div>
      )}
    </PageLayout>
  );
};

export default InventoryPage;